import { getSpreadsheetCommand, type SpreadsheetCommandId, type SpreadsheetCommandState } from './commandRegistry'

export type RowColumnContextMenuKind = 'row' | 'column'

export type RibbonStructuralSelectionKind = 'cell' | 'row' | 'column' | 'all'

export type RibbonStructuralSelection = {
  col: number
  row: number
  endCol: number
  endRow: number
}

export type RibbonStructuralCommandId = Extract<SpreadsheetCommandId, 'insert-cells' | 'delete-cells'>

export type RibbonStructuralCommandPlan =
  | {
    kind: 'column'
    action: 'insert_left' | 'delete'
    selection: { startCol: number; endCol: number }
  }
  | {
    kind: 'row'
    action: 'insert_above' | 'delete'
    selection: { startRow: number; endRow: number }
  }

export type RowColumnContextMenuAction =
  | 'insert_left'
  | 'insert_right'
  | 'insert_above'
  | 'insert_below'
  | 'delete'
  | 'clear'
  | 'format-cells'
  | 'column-width'
  | 'autofit-column-width'
  | 'row-height'
  | 'autofit-row-height'
  | 'find'
  | 'replace'

export type RowColumnContextMenuEntry =
  | { type: 'separator' }
  | {
    action: RowColumnContextMenuAction
    label: string
    title: string
    disabled?: boolean
    commandId?: SpreadsheetCommandId
    shortcut?: string
  }

type CommandStateProjection = Pick<SpreadsheetCommandState, 'enabled' | 'disabledReason'>

function formatCountLabel(count: number, singular: string, plural: string): string {
  return count === 1 ? singular : `${count} ${plural}`
}

function disabledReasonSuffix(state: CommandStateProjection): string {
  return state.disabledReason ? ` ${state.disabledReason}.` : ''
}

function boundedCount(value: unknown): number {
  return Math.max(0, Math.floor(Number(value) || 0))
}

export function deriveRibbonStructuralSelectionKind(args: {
  selection?: RibbonStructuralSelection | null
  rowCount: number
  colCount: number
  mockMode?: boolean
}): RibbonStructuralSelectionKind | undefined {
  if (args.mockMode) return 'cell'
  const selection = args.selection
  if (!selection) return undefined
  const startCol = Math.min(selection.col, selection.endCol)
  const endCol = Math.max(selection.col, selection.endCol)
  const startRow = Math.min(selection.row, selection.endRow)
  const endRow = Math.max(selection.row, selection.endRow)
  const maxCol = Math.max(0, boundedCount(args.colCount) - 1)
  const maxRow = Math.max(0, boundedCount(args.rowCount) - 1)
  const fullRows = startCol === 0 && endCol >= maxCol
  const fullCols = startRow === 0 && endRow >= maxRow
  if (fullRows && fullCols) return 'all'
  if (fullRows) return 'row'
  if (fullCols) return 'column'
  return 'cell'
}

export function buildRibbonStructuralCommandPlan(args: {
  commandId: RibbonStructuralCommandId
  selection?: RibbonStructuralSelection | null
  selectionKind?: RibbonStructuralSelectionKind
}): RibbonStructuralCommandPlan | undefined {
  const selection = args.selection
  if (!selection) return undefined
  if (args.selectionKind === 'column') {
    const startCol = Math.min(selection.col, selection.endCol)
    const endCol = Math.max(selection.col, selection.endCol)
    return {
      kind: 'column',
      action: args.commandId === 'insert-cells' ? 'insert_left' : 'delete',
      selection: { startCol, endCol },
    }
  }
  if (args.selectionKind === 'row') {
    const startRow = Math.min(selection.row, selection.endRow)
    const endRow = Math.max(selection.row, selection.endRow)
    return {
      kind: 'row',
      action: args.commandId === 'insert-cells' ? 'insert_above' : 'delete',
      selection: { startRow, endRow },
    }
  }
  return undefined
}

export function buildRowColumnContextMenuEntries(args: {
  kind: RowColumnContextMenuKind
  count: number
  totalCount: number
  hasStructuralActions: boolean
  insertState: CommandStateProjection
  deleteState: CommandStateProjection
  formatState: CommandStateProjection
  resizeBlockedByProtection: boolean
  resizeProtectionReason?: string | null
}): RowColumnContextMenuEntry[] {
  const count = Math.max(1, Math.floor(args.count))
  const totalCount = Math.max(0, Math.floor(args.totalCount))
  const insertCommand = getSpreadsheetCommand('insert-cells')
  const deleteCommand = getSpreadsheetCommand('delete-cells')
  const clearCommand = getSpreadsheetCommand('clear-contents')
  const formatCommand = getSpreadsheetCommand('format-cells')
  const findCommand = getSpreadsheetCommand('find')
  const replaceCommand = getSpreadsheetCommand('replace')
  const hasStructuralActions = Boolean(args.hasStructuralActions)
  const canDelete = hasStructuralActions && count < totalCount
  const resizeReason = args.resizeProtectionReason ?? ''

  if (args.kind === 'column') {
    const columnLabel = formatCountLabel(count, 'Column', 'Columns')
    return [
      {
        action: 'insert_left',
        commandId: 'insert-cells',
        label: `Insert ${columnLabel} Left`,
        title: `${insertCommand.description} Insert before the selected column range.${disabledReasonSuffix(args.insertState)}`,
        disabled: !args.insertState.enabled,
      },
      {
        action: 'insert_right',
        commandId: 'insert-cells',
        label: `Insert ${columnLabel} Right`,
        title: `${insertCommand.description} Insert after the selected column range.${disabledReasonSuffix(args.insertState)}`,
        disabled: !args.insertState.enabled,
      },
      {
        action: 'delete',
        commandId: 'delete-cells',
        label: `${deleteCommand.label} ${columnLabel}`,
        title: `${deleteCommand.description}${disabledReasonSuffix(args.deleteState)}`,
        disabled: !canDelete || !args.deleteState.enabled,
      },
      { type: 'separator' },
      {
        action: 'clear',
        commandId: 'clear-contents',
        label: clearCommand.label,
        title: clearCommand.description,
        disabled: !hasStructuralActions,
      },
      {
        action: 'format-cells',
        commandId: 'format-cells',
        label: formatCommand.label,
        shortcut: formatCommand.shortcut,
        title: formatCommand.description,
        disabled: !args.formatState.enabled,
      },
      { type: 'separator' },
      {
        action: 'column-width',
        label: 'Column Width...',
        title: args.resizeBlockedByProtection
          ? `Set the width for the selected column range. ${resizeReason}`
          : 'Set the width for the selected column range.',
        disabled: args.resizeBlockedByProtection,
      },
      {
        action: 'autofit-column-width',
        label: 'AutoFit Column Width',
        title: args.resizeBlockedByProtection
          ? `Resize selected columns to fit visible cell contents. ${resizeReason}`
          : 'Resize selected columns to fit visible cell contents.',
        disabled: args.resizeBlockedByProtection,
      },
      { type: 'separator' },
      {
        action: 'find',
        commandId: 'find',
        label: findCommand.label,
        shortcut: findCommand.shortcut,
        title: findCommand.description,
      },
      {
        action: 'replace',
        commandId: 'replace',
        label: replaceCommand.label,
        shortcut: replaceCommand.shortcut,
        title: replaceCommand.description,
      },
    ]
  }

  const rowLabel = formatCountLabel(count, 'Row', 'Rows')
  return [
    {
      action: 'insert_above',
      commandId: 'insert-cells',
      label: `Insert ${rowLabel} Above`,
      title: `${insertCommand.description} Insert before the selected row range.${disabledReasonSuffix(args.insertState)}`,
      disabled: !args.insertState.enabled,
    },
    {
      action: 'insert_below',
      commandId: 'insert-cells',
      label: `Insert ${rowLabel} Below`,
      title: `${insertCommand.description} Insert after the selected row range.${disabledReasonSuffix(args.insertState)}`,
      disabled: !args.insertState.enabled,
    },
    {
      action: 'delete',
      commandId: 'delete-cells',
      label: `${deleteCommand.label} ${rowLabel}`,
      title: `${deleteCommand.description}${disabledReasonSuffix(args.deleteState)}`,
      disabled: !canDelete || !args.deleteState.enabled,
    },
    { type: 'separator' },
    {
      action: 'clear',
      commandId: 'clear-contents',
      label: clearCommand.label,
      title: clearCommand.description,
      disabled: !hasStructuralActions,
    },
    {
      action: 'format-cells',
      commandId: 'format-cells',
      label: formatCommand.label,
      shortcut: formatCommand.shortcut,
      title: formatCommand.description,
      disabled: !args.formatState.enabled,
    },
    { type: 'separator' },
    {
      action: 'row-height',
      label: 'Row Height...',
      title: args.resizeBlockedByProtection
        ? `Set the height for the selected row range. ${resizeReason}`
        : 'Set the height for the selected row range.',
      disabled: args.resizeBlockedByProtection,
    },
    {
      action: 'autofit-row-height',
      label: 'AutoFit Row Height',
      title: args.resizeBlockedByProtection
        ? `Resize selected rows to fit visible cell contents. ${resizeReason}`
        : 'Resize selected rows to fit visible cell contents.',
      disabled: args.resizeBlockedByProtection,
    },
    { type: 'separator' },
    {
      action: 'find',
      commandId: 'find',
      label: findCommand.label,
      shortcut: findCommand.shortcut,
      title: findCommand.description,
    },
    {
      action: 'replace',
      commandId: 'replace',
      label: replaceCommand.label,
      shortcut: replaceCommand.shortcut,
      title: replaceCommand.description,
    },
  ]
}
